import './levelPicker.css';

/** Selectable Korean handicap levels (points to clear), shared across the UI. */
export const GAME_LEVELS = [30, 50, 80, 100, 120, 150, 200, 250, 300, 400, 500];

const ROW = 40; // px per row
const VISIBLE = 5; // odd: the centre row is the selection
const PAD = ((VISIBLE - 1) / 2) * ROW;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

/**
 * A vertical scroll-WHEEL picker (iOS-style): values scroll under a fixed centre
 * band; whichever lands in the centre is selected. `onChange` fires when the
 * centred value settles; `set(v)` scrolls a value into the centre; `value()` reads
 * the current selection. Clicking a row snaps it to the centre.
 */
export function buildWheelPicker(
  values: number[],
  onChange: (value: number) => void
): { el: HTMLElement; set: (value: number) => void; value: () => number } {
  const wrap = el('div', 'wheel-wrap');
  wrap.style.height = ROW * VISIBLE + 'px';

  const scroller = el('div', 'wheel');
  const list = el('div', 'wheel-list');
  const topPad = el('div', 'wheel-spacer');
  topPad.style.height = PAD + 'px';
  const botPad = el('div', 'wheel-spacer');
  botPad.style.height = PAD + 'px';
  list.appendChild(topPad);
  const items = values.map((v) => {
    const it = el('button', 'wheel-item');
    it.style.height = ROW + 'px';
    it.textContent = String(v);
    it.onclick = () => snapTo(values.indexOf(v));
    list.appendChild(it);
    return it;
  });
  list.appendChild(botPad);
  scroller.appendChild(list);

  const band = el('div', 'wheel-band');
  band.style.height = ROW + 'px';
  band.style.top = PAD + 'px';

  wrap.append(scroller, band);

  let current = values[0];
  let suppress = false; // ignore scroll events caused by set()/snap

  const indexFromScroll = (): number => Math.max(0, Math.min(values.length - 1, Math.round(scroller.scrollTop / ROW)));

  const render = (centerIdx: number): void => {
    items.forEach((it, i) => {
      const d = Math.abs(i - centerIdx);
      it.classList.toggle('on', i === centerIdx);
      it.style.opacity = String(Math.max(0.2, 1 - d * 0.3));
      it.style.transform = `scale(${Math.max(0.78, 1 - d * 0.09)})`;
    });
  };

  function snapTo(i: number): void {
    i = Math.max(0, Math.min(values.length - 1, i));
    scroller.scrollTo({ top: i * ROW, behavior: 'smooth' });
    // onChange fires from the scroll-settle handler once it lands
  }

  let settle: ReturnType<typeof setTimeout>;
  scroller.addEventListener('scroll', () => {
    render(indexFromScroll()); // live visual while scrolling
    clearTimeout(settle);
    settle = setTimeout(() => {
      const i = indexFromScroll();
      if (suppress) {
        suppress = false;
      } else if (values[i] !== current) {
        current = values[i];
        onChange(current);
      }
      render(i);
    }, 90);
  });

  const set = (v: number): void => {
    const i = values.indexOf(v);
    if (i < 0) return;
    current = v;
    suppress = true;
    scroller.scrollTop = i * ROW; // jump (no onChange)
    render(i);
  };

  return { el: wrap, set, value: () => current };
}
